import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ITrackingTypes } from '@r3f-component-data-structure';
import * as Sentry from '@sentry/react';
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import Info20 from './assets/icons/Info20';
import BrowserError from './components/dom/BrowserError/BrowserError';
import * as settings from './settings';
import { store } from './store/index';
const App = React.lazy(() => import('./components/App/App'));
import { connectToCds } from './sync/index';
import { isSupportedBrowser } from './utils/compatibility';
import { MAINTENANCE_INTERVAL } from './utils/constants';
import { updateProjectScreenshot } from './utils/general';
import { performMaintenanceCheck } from './utils/maintenance';
import './index.scss';
// Check browser compatibility & show upgrade message if browser can't support designer
const browserIsCompatible = isSupportedBrowser();
// Initialize sentry before React
// TODO: add Sentry's React router integration https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/
Sentry.init({
    dsn: settings.SENTRY_DSN,
    environment: settings.ENVIRONMENT,
    release: settings.VERSION,
    integrations: [Sentry.browserTracingIntegration(), Sentry.browserProfilingIntegration(), Sentry.httpClientIntegration(), Sentry.feedbackIntegration({ autoInject: false })],
    tracesSampleRate: 0.9,
    normalizeDepth: 5,
    beforeSend: (event, hint) => {
        if (settings.ENVIRONMENT === 'local') {
            console.log(hint.originalException || hint.syntheticException);
            return null; // this drops the event and nothing will be sent to sentry
        }
        // Filter out known large HTTP payload issues from filestore domains
        // These are expected for large media files/assets (3D models, videos, 360 content, etc.)
        // We still track them but with much reduced noise to focus on unexpected large payloads
        // 
        // Based on analysis of issue DESIGNER-7E0, filestore requests can come from:
        // - filestore.zapworkscds.com (production)
        // - filestore.staging.zapworkscds.com (staging)
        // - filestore.dev.zapworkscds.com (development)
        // - zapworkscds.com/.../filestore/jwt/ (JWT token endpoints)
        // Check for large HTTP payload events
        const isLargeHttpPayloadEvent = Array.isArray(event.fingerprint) && event.fingerprint.some(fp => fp.includes('large-http-payload'));
        // Check spans for filestore URLs
        if (isLargeHttpPayloadEvent) {
            const spans = event.spans || [];
            const hasFilestoreSpan = spans.some(span => {
                const url = span.data?.['http.url'] ||
                    span.data?.url ||
                    span.description ||
                    span.data?.['server.address'] || '';
                if (typeof url !== 'string')
                    return false;
                // Check for all filestore-related URLs including:
                // 1. Direct filestore subdomains
                // 2. JWT endpoints under zapworkscds.com
                // 3. All environment variations (staging, dev, production)
                return (url.includes('filestore.staging.zapworkscds.com') ||
                    url.includes('filestore.dev.zapworkscds.com') ||
                    url.includes('filestore.zapworkscds.com') ||
                    (url.includes('zapworkscds.com') && url.includes('/filestore/')));
            });
            if (hasFilestoreSpan) {
                // Reduce noise for filestore large payloads - only send 10% of these events
                // (matching the tracesSampler rate for consistency)
                // if (Math.random() > 0.1) {
                return null;
                // }
                // // Add tags to identify filtered events for debugging
                // event.tags = {
                // 	...event.tags,
                // 	'filtered_large_payload': 'filestore',
                // 	'sample_rate': '0.1'
                // }
            }
        }
        return event;
    },
    tracesSampler: ({ name, attributes }) => {
        // Reduce sample rate for known large payload endpoints
        if (name.includes("entity-load-error"))
            return 0.25;
        // Prevent tracing for filestore requests as they're expected to be large
        if (attributes && attributes['http.url']) {
            const url = attributes['http.url'];
            // Check for all filestore-related URLs including:
            // 1. Direct filestore subdomains
            // 2. JWT endpoints under zapworkscds.com
            // 3. All environment variations (staging, dev, production)
            if (url.includes('filestore.staging.zapworkscds.com') ||
                url.includes('filestore.dev.zapworkscds.com') ||
                url.includes('filestore.zapworkscds.com') ||
                (url.includes('zapworkscds.com') && url.includes('/filestore/'))) {
                return 0; // Prevent tracing for filestore requests
            }
        }
        // Otherwise, inherit the sample sampling decision of the incoming trace, or use a fallback sampling rate.
        return 0.9;
    }
});
// Prevent N+1 API Call error for data urls
Sentry.browserTracingIntegration({
    shouldCreateSpanForRequest: (url) => {
        return !url.startsWith('blob:');
    },
});
// Add extra context to all Sentry exceptions
Sentry.addEventProcessor(function (event, hint) {
    const activeSceneId = store.getState().userReducer.activeSceneId;
    const activeSceneType = activeSceneId ? store.getState().contentReducer?.contentDoc?.componentsById?.[activeSceneId]?.trackingType : null;
    const additionalContext = {
        socketConnected: store.getState().userReducer.socketConnected,
        undoDisabled: store.getState().userReducer.undoDisabled,
        redoDisabled: store.getState().userReducer.redoDisabled,
        canvasLoaded: store.getState().userReducer.canvasLoaded,
        projectLoadingProgress: store.getState().userReducer.projectLoadingProgress,
        projectLoadingFailure: store.getState().userReducer.projectLoadingFailure,
        selectedEntityIds: store.getState().userReducer.selectedEntityIds,
        isScreenRelativeMode: store.getState().userReducer.isScreenRelativeMode,
        is3dMode: store.getState().userReducer.is3dMode,
        entityMenuDragActive: store.getState().userReducer.entityMenuDragActive,
        hotKeys: store.getState().userReducer.hotKeys,
        activeSceneType,
        hint,
    };
    event.extra = { ...additionalContext, ...event.extra };
    return event;
});
// show maintenance toast if CMS is going to be in maintenance mode
setInterval(performMaintenanceCheck, MAINTENANCE_INTERVAL);
// set local storage particle state to show debug section
const urlParams = new URLSearchParams(window.location.search);
const debugParticles = urlParams.get('debugparticles');
if (typeof debugParticles !== 'undefined' && debugParticles !== null) {
    localStorage.setItem('debugparticles', debugParticles);
}
const beforeUnloadHandler = (_) => {
    const projectId = store?.getState?.()?.userReducer?.project?.id;
    const currentActiveSceneId = store?.getState?.()?.userReducer?.activeSceneId || '';
    const currentIsArModeDisabled = store?.getState?.()?.contentReducer?.contentDoc?.componentsById?.[currentActiveSceneId]?.trackingType === ITrackingTypes.noTrackingScreen;
    if (!!projectId && !!currentActiveSceneId)
        updateProjectScreenshot(projectId, currentIsArModeDisabled);
};
window.addEventListener('beforeunload', beforeUnloadHandler);
// Render app
(async (store) => {
    const container = document.getElementById('root') || document.createElement('div');
    const root = createRoot(container);
    if (!browserIsCompatible)
        return root.render(_jsx(BrowserError, {}));
    try {
        await connectToCds(store);
    }
    catch (err) {
        console.error(err);
        Sentry.captureException(err);
        return root.render(getLoadingErrorElement());
    }
    // At this point we can know that the content doc is empty ( or otherwise malformed )
    // Log to Sentry if we experience contentDoc = { } situation
    const contentDoc = store.getState().contentReducer.contentDoc;
    if (Object.entries(contentDoc).length < 2) {
        Sentry.captureException(new Error('Empty content doc - manual exception.'));
        return root.render(getLoadingErrorElement());
    }
    // Don't` render at 5% because it leads to a flicker when it has to re-render
    // index.tsx gets reloaded when syncdoc changes the window.location just before action SET_PROJECT_LOADING_PROGRESS with 20% gets dispatched
    if (store.getState().userReducer.projectLoadingProgress <= 5)
        return null;
    root.render(_jsx(Sentry.ErrorBoundary, { children: _jsx(Provider, { store: store, children: _jsx(React.Suspense, { fallback: null, children: _jsx(App, { store: store }) }) }) }));
})(store);
function getLoadingErrorElement() {
    return _jsx("div", { style: {
            display: 'flex',
            width: '100vw',
            height: '100vh',
            justifyContent: 'center',
            alignItems: 'center',
        }, children: _jsxs("div", { style: {
                backgroundColor: '#f2f2f2',
                borderRadius: '24px',
                color: '#073158',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '100px',
            }, children: [_jsx(Info20, { height: "75", width: "75" }), _jsx("h1", { style: { marginTop: '50px' }, children: "Oops! Something went wrong" }), _jsx("p", { style: { fontSize: '20px' }, children: "Designer encountered an error and was unable to load. Please refresh and try again." })] }) });
}
